import { Component, createRef } from 'react';
import isEqual from 'lodash-es/isEqual';
import { connect } from 'react-redux';
import autoBind from 'react-autobind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { faFilter } from '@fortawesome/pro-solid-svg-icons/faFilter';

import withMetatag from '../../../../decorators/metadataDecorators/metatagsTitleDecorator';
import withComponentName from '../../../../decorators/withComponentName';
import {
  getPresentationsData, getNewPresentationsData, resetPeriodsFilter,
} from '../../../../actions/investorRelations.actions';
import { setWatchlistLoadStatus } from '../../../../actions/watchlistActions';
import { queryItemWatchlistParser } from '../../../../helpers/watchlistHelperFunctions';
import { commonAPIDataLimit } from '../../../../data/webPageData';
import { presentationsParamGenerate } from '../../../../helpers/identifierParamHelpers';

import FilterButton from '../../../Buttons/FilterButton';
import FilterBar from './FilterBar';
import Preloader from '../../../Preloader';
import StoryCardLists from '../../../SEO_pages/CommonComponent/StoryCardLists';
import WLTypesSection from './WLTypesSection';

import Styles from './styles.module.scss';

const BIG_SCREEN_SIZE = 2800;

class InvestorRelations extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFilterOpen: false,
      isInitialPresentationsCall: false,
      isShowMoreActive: false,
      currentPage: 1,
      hasNextPage: true,
      scrollDivTop: 0,
      extraProps: '',
      isReset: false,
      isSingleTopic: false,
    };

    autoBind(this);
    this.mounted = false;
    this.newPresentationsFetching = false;
    this.presentationsListsRef = createRef();
  }

  componentDidMount() {
    this.mounted = true;
    const { extraProps, isSingleTopic } = this.getExtraProps();
    this.setState({
      extraProps,
      isSingleTopic,
    }, () => {
      this.loadInvestorData();
    });
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.onResize();
  }

  componentDidUpdate(prevProps, prevState) {
    const { investorRelations, activeWatchlistTopics } = this.props;
    const { investorRelations: prevInvestorRelations } = prevProps;

    const propsParam = {
      isAllTypesSelected: investorRelations.isAllTypesSelected,
      selectedTypes: investorRelations.selectedTypes,
      isAllYearsSelected: investorRelations.isAllYearsSelected,
      fromYear: investorRelations.fromYear,
      toYear: investorRelations.toYear,
      isAllPeriodsSelected: investorRelations.isAllPeriodsSelected,
      selectedPeriods: investorRelations.selectedPeriods,
    };
    const prevPropsParam = {
      isAllTypesSelected: prevInvestorRelations.isAllTypesSelected,
      selectedTypes: prevInvestorRelations.selectedTypes,
      isAllYearsSelected: prevInvestorRelations.isAllYearsSelected,
      fromYear: prevInvestorRelations.fromYear,
      toYear: prevInvestorRelations.toYear,
      isAllPeriodsSelected: prevInvestorRelations.isAllPeriodsSelected,
      selectedPeriods: prevInvestorRelations.selectedPeriods,
    };

    if (!isEqual(propsParam, prevPropsParam)) {
      this.loadInvestorData();
    }
    if (!isEqual(activeWatchlistTopics, prevProps.activeWatchlistTopics)) {
      const { extraProps, isSingleTopic } = this.getExtraProps();
      this.setState({
        extraProps,
        isSingleTopic,
      }, () => {
        this.loadInvestorData();
      });
    }
    const { isReset } = this.state;
    if (isReset && prevState.isReset !== isReset) {
      this.setState({
        isReset: false,
      });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.presentationsListsRef && this.presentationsListsRef.current) {
      this.presentationsListsRef.current.removeEventListener('scroll', this.scrollPresentations);
    }
  }

  onResize() {
    this.setScrollUpButtonPosition();
  }

  getExtraProps() {
    const { activeWatchlistTopics } = this.props;

    const extraProps = activeWatchlistTopics
      .filter((f) => f.entity_type === 'Stocks')
      .map(({
        hidden, query, name, tickers,
      }) => {
        if (hidden) return;

        const { name: slugName } = query ? queryItemWatchlistParser(query) : { name };
        const identifiers = presentationsParamGenerate({
          name: slugName,
          tickers: (tickers || []),
        });

        return identifiers;
      }).filter((f) => f).join(',');

    return {
      extraProps,
      isSingleTopic: activeWatchlistTopics.filter(({ hidden }) => !hidden).length === 1,
    };
  }

  setScrollUpButtonPosition() {
    if (!this.presentationsListsRef || !this.presentationsListsRef.current) return;

    const storiesRect = this.presentationsListsRef.current.getBoundingClientRect();
    let topCSS = storiesRect.height - 48;
    if (storiesRect.top > 0) {
      topCSS = storiesRect.height - storiesRect.top - 48;
    }
    this.setState({ scrollUpTop: topCSS });
  }

  scrollToStart() {
    const WLTabBar = document.getElementById('watchlists_tab_bar_holder');
    let offset = 0;
    if (WLTabBar) {
      const tabTop = WLTabBar.getBoundingClientRect().top;
      offset = tabTop <= 0 ? window.pageYOffset + tabTop : 0;
    }

    if (offset) {
      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }
    if (this.presentationsListsRef && this.presentationsListsRef.current) {
      this.presentationsListsRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  handleScroll() {
    const { isShowMoreActive } = this.state;
    if (!isShowMoreActive) return;

    this.setScrollUpButtonPosition();
  }

  async scrollPresentations({ currentTarget }) {
    const { scrollDivTop } = this.state;
    const { statePromo } = this.props;
    const { height } = statePromo;
    this.setScrollUpButtonPosition();

    const { top } = currentTarget.getBoundingClientRect();

    // Tab height 40 + story margin 15 + 5 extra space = 65
    if (top - height - 60 > 0 && currentTarget.scrollTop > scrollDivTop) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop < currentTarget.scrollTop) {
        const divTopSpace = top + scrollTop;
        const isOutSideRange = currentTarget.scrollTop > divTopSpace;
        window.scrollTo({
          top: isOutSideRange ? divTopSpace : currentTarget.scrollTop,
        });
      }
    }
    this.setState({
      scrollDivTop: currentTarget.scrollTop,
    });

    if (currentTarget.scrollTop + currentTarget.clientHeight > currentTarget.scrollHeight - 2000) {
      this.newStoriesFetching = true;
      await this.loadNewPresentationsData();
      this.newStoriesFetching = false;
    }
  }

  async loadNewPresentationsData(seeMore) {
    const { hasNextPage, currentPage, extraProps } = this.state;
    const {
      getNewPresentationsData: propsGetNewPresentations,
      loading, presentationsPageLoad,
    } = this.props;

    if (
      this.newPresentationsFetching || !hasNextPage || loading || presentationsPageLoad
      || !extraProps
    ) return;

    const param = {
      per: commonAPIDataLimit,
      page: seeMore ? 1 : currentPage + 1,
      identifiers: extraProps,
    };

    this.newPresentationsFetching = true;
    const data = await propsGetNewPresentations(param);
    this.newPresentationsFetching = false;

    if (!this.mounted) return;
    if (data.length < commonAPIDataLimit) {
      this.setState({
        hasNextPage: false,
      });
    } else {
      this.setState({
        hasNextPage: true,
        currentPage: seeMore ? 1 : currentPage + 1,
      });
    }
  }

  loadInvestorData(isRefresh) {
    const {
      getPresentationsData: propsGetData,
      setWatchlistLoadStatus, wlPageLoadingStep,
    } = this.props;
    const { extraProps, isShowMoreActive } = this.state;

    if (!extraProps) {
      return this.setState({
        isInitialPresentationsCall: true,
        currentPage: 1,
      });
    }

    const dataLength = isShowMoreActive ? commonAPIDataLimit : 5;
    const props = {
      identifiers: extraProps,
      per: commonAPIDataLimit,
      isRefresh,
    };
    propsGetData(props, { isInitialCall: true })
      .then((data) => {
        this.setState({
          hasNextPage: data.length >= dataLength,
        });
        if (wlPageLoadingStep !== 'LOADED') {
          setWatchlistLoadStatus('LOADED');
        }
      })
      .catch(() => {

      });
    this.setState({
      isInitialPresentationsCall: true,
      currentPage: 1,
    });
  }

  handleFilterRefreshClick() {
    const { piwikEnabled } = this.props;
    this.loadInvestorData(true);

    if (piwikEnabled) {
      _paq.push(['trackEvent', 'WL IR', 'Refresh:']);
    }
  }

  handleFilterButtonClick() {
    const { isFilterOpen } = this.state;
    this.setState({
      isFilterOpen: !isFilterOpen,
    });
  }

  handleFilterButtonClose() {
    this.setState({
      isFilterOpen: false,
    });
  }

  handleShowMoreClick() {
    this.setState(({ isShowMoreActive }) => ({
      isShowMoreActive: !isShowMoreActive,
    }));
  }

  handleReset() {
    const { resetPeriodsFilter: propsResetAll, piwikEnabled } = this.props;

    propsResetAll();
    this.setState({
      isReset: true,
    });

    if (piwikEnabled) {
      _paq.push(['trackEvent', 'ESG filters', 'Reset filters']);
    }
  }

  handleRef(value) {
    if (value) {
      value.addEventListener('scroll', this.scrollPresentations);
      this.presentationsListsRef.current = value;
    }
  }

  render() {
    const {
      isFilterOpen, isReset, isInitialPresentationsCall, isShowMoreActive,
      hasNextPage, scrollUpTop, scrollDivTop, isSingleTopic,
    } = this.state;
    const {
      loading, presentations, presentationsPageLoad, newPresentationsLoading,
      activeWatchlistTopics, width, piwikEnabled, wlPageLoadingStep,
    } = this.props;

    const presentationsData = !isShowMoreActive && width < BIG_SCREEN_SIZE
      ? presentations.slice(0, 5) : presentations;
    const showCaughtUp = presentations.length <= 5 ? hasNextPage : hasNextPage || !isShowMoreActive;

    if (wlPageLoadingStep !== 'LOADED') {
      return null;
    }

    return (
      <div className={Styles.investor_relations_container}>
        <div className={Styles.investor_relations_content}>
          <div className={Styles.investor_relations_stories_container}>
            <div className={Styles.filter_buttons}>
              <FilterButton
                icon={<FontAwesomeIcon icon={faRedo} />}
                content="Refresh"
                onClick={this.handleFilterRefreshClick}
              />
              <FilterButton
                icon={<FontAwesomeIcon icon={faFilter} />}
                content="Filters"
                active={isFilterOpen}
                onClick={this.handleFilterButtonClick}
              />
            </div>
            <FilterBar
              isFilterOpen={isFilterOpen}
              isReset={isReset}
              onReset={this.handleReset}
              isIR={width >= BIG_SCREEN_SIZE}
              handleFilterClose={this.handleFilterButtonClose}
              piwikEnabled={piwikEnabled}
            />
            {
              loading || (presentationsPageLoad && !presentations.length) ? (
                <div className={Styles.investor_loader_holder}>
                  <Preloader loading />
                </div>
              ) : (
                <div className={Styles.investor_relations_holder}>
                  <StoryCardLists
                    isWatchlists
                    showCompanyTag
                    cardURL="/directory/investor-relations"
                    presentations={presentationsData}
                    presentationsPageLoad={presentationsPageLoad}
                    isInitialPresentationsCall={isInitialPresentationsCall}
                    newPresentationsLoading={newPresentationsLoading}
                    hasNextPage={showCaughtUp}
                    ref={this.handleRef}
                    scrollDivTop={scrollDivTop}
                    scrollUpTop={scrollUpTop}
                    scrollToStart={this.scrollToStart}
                    isSingleTopic={isSingleTopic}
                    isIR={width >= BIG_SCREEN_SIZE}
                    bigScreen={BIG_SCREEN_SIZE}
                  />
                  {presentations.length && presentations.length > 5 && width < BIG_SCREEN_SIZE ? (
                    <div className={Styles.show_more_btn} onClick={this.handleShowMoreClick}>
                      {!isShowMoreActive ? 'Show More ' : 'Show Less'}
                    </div>
                  ) : null}
                </div>
              )
            }
          </div>
          <WLTypesSection
            activeWatchlistTopics={activeWatchlistTopics}
            isSingleTopic={isSingleTopic}
            isIR={width >= BIG_SCREEN_SIZE}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({
  investorRelations, promobar, common, watchlistReducer,
}) => ({
  investorRelations,
  loading: investorRelations.loading,
  presentations: investorRelations.presentations,
  presentationsPageLoad: investorRelations.presentationsPageLoad,
  newPresentationsLoading: investorRelations.newPresentationsLoading,
  statePromo: promobar,
  scrollUpPressed: common.scrollUpPressed,
  activeWatchlistTopics: watchlistReducer.activeWatchlistTopics,
  wlPageLoadingStep: watchlistReducer.wlPageLoadingStep,
  width: common.width,
});

const mapDispatchToProps = {
  getPresentationsData,
  getNewPresentationsData,
  resetPeriodsFilter,
  setWatchlistLoadStatus,
};

export default withComponentName(
  connect(mapStateToProps, mapDispatchToProps)(withMetatag(InvestorRelations, 'watchlists/investor-relations')),
);
